import {
  UserRound,
  LayoutDashboard, Users, UserSearch, CalendarCheck, CalendarDays, Wallet,
  Target, HandCoins, Package, FileText, BarChart3, Settings, ShieldCheck, Timer,
  Inbox, Stamp, Scale, ReceiptText, Handshake, LifeBuoy, Megaphone, ScrollText, DoorOpen,
  type LucideIcon,
} from 'lucide-react'

/** A leaf: something you can actually navigate to. */
export interface NavChild {
  /** i18n key under `navChild.` — e.g. `attendance.daily`. */
  key: string
  to: string
  /** Hidden unless the user holds this permission. */
  permission?: string
  /** Hidden unless the user holds at least one of these — on top of {@link permission}. */
  anyPermission?: string[]
  /**
   * The company directory: shown when the company has opened it to employees, or to anybody
   * holding employee:read-all, whom it was never closed to.
   */
  requiresDirectory?: boolean
}

export interface NavItem {
  /** i18n key under `nav.` */
  key: string
  icon: LucideIcon
  /** Where the module itself goes. Omitted when it only exists to hold children. */
  to?: string
  permission?: string
  anyPermission?: string[]
  children?: NavChild[]
}

/** What decides whether an entry shows. */
export interface NavContext {
  has: (permission: string) => boolean
  directoryOpen: boolean
}

/**
 * Whether one entry is shown.
 *
 * <p>The permissions named here are the ones the screen's own endpoints check. A self-service
 * permission — every employee's — opens the employee's own screens; a module's register, its
 * set-up and its company-wide figures need the permission that makes them somebody's job, and
 * the menu no longer offers an employee a screen of colleagues that the server now refuses.
 */
export function isNavVisible(entry: NavChild | NavItem, context: NavContext): boolean {
  if (entry.permission && !context.has(entry.permission)) return false
  if (entry.anyPermission && !entry.anyPermission.some((permission) => context.has(permission))) return false
  if ('requiresDirectory' in entry && entry.requiresDirectory
      && !context.directoryOpen && !context.has('employee:read-all')) return false
  return true
}

/**
 * The sidebar, as a tree.
 *
 * <p>Modules with children expand rather than navigate: a parent that both navigates and
 * toggles makes the click ambiguous, and the destination is always one of its children
 * anyway.
 *
 * <p>Permissions are applied to both levels, and a module whose children are all hidden
 * disappears with them — a module you can open to find nothing is worse than one that is
 * not there. Filtering is for usability only; every route is guarded again by
 * `RequirePermission`, and the API by `@PreAuthorize`.
 *
 * <p>Not everything here is built. Where a screen is still a placeholder the route exists
 * and says which phase it belongs to, because a menu entry that 404s is worse than one that
 * explains itself.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    // First, and above the modules: an employee arriving here should not have to know which
    // module their question belongs to before they can ask it.
    key: 'portal',
    icon: UserRound,
    permission: 'dashboard:view',
    children: [
      { key: 'portal.home', to: '/portal', permission: 'dashboard:view' },
      { key: 'portal.punch', to: '/portal/punch', permission: 'attendance:punch-self' },
      { key: 'portal.myTeam', to: '/portal/my-team', permission: 'dashboard:view' },
      { key: 'portal.delegation', to: '/portal/delegation', permission: 'delegation:manage' },
      { key: 'portal.devices', to: '/portal/devices', permission: 'dashboard:view' },
    ],
  },
  {
    key: 'dashboard',
    to: '/',
    icon: LayoutDashboard,
    permission: 'dashboard:view',
    // Head counts, who is on leave, the department chart: the company's figures. An employee's
    // own are on My workspace.
    anyPermission: ['employee:read-all', 'attendance:read-all', 'leave:read-all', 'payroll:read-all'],
  },
  {
    key: 'employees',
    icon: Users,
    permission: 'employee:read',
    children: [
      { key: 'employees.directory', to: '/employees', permission: 'employee:read', requiresDirectory: true },
      { key: 'employees.orgChart', to: '/employees/org-chart', permission: 'employee:read', requiresDirectory: true },
      { key: 'employees.transfers', to: '/employees/transfers', permission: 'employee:transfer' },
      { key: 'employees.departments', to: '/org/departments', permission: 'org:read', anyPermission: ['org:write', 'employee:read-all'] },
      { key: 'employees.divisions', to: '/org/divisions', permission: 'org:read', anyPermission: ['org:write', 'employee:read-all'] },
      { key: 'employees.legalEntities', to: '/org/legal-entities', permission: 'org:read', anyPermission: ['org:write', 'employee:read-all'] },
      { key: 'employees.establishments', to: '/org/establishments', permission: 'org:read', anyPermission: ['org:write', 'employee:read-all'] },
      { key: 'employees.masters', to: '/org/masters', permission: 'org:read', anyPermission: ['org:write', 'employee:read-all'] },
      { key: 'employees.positions', to: '/org/positions', permission: 'workforce:read' },
    ],
  },
  {
    key: 'recruitment',
    icon: UserSearch,
    permission: 'recruitment:read',
    children: [
      { key: 'recruitment.overview', to: '/recruitment', permission: 'recruitment:read' },
      { key: 'recruitment.requisitions', to: '/recruitment/requisitions', permission: 'recruitment:read' },
      { key: 'recruitment.candidates', to: '/recruitment/candidates', permission: 'candidate:read' },
      { key: 'recruitment.offers', to: '/recruitment/offers', permission: 'offer:read' },
      { key: 'recruitment.settings', to: '/recruitment/settings', permission: 'recruitment:read' },
    ],
  },
  {
    key: 'attendance',
    icon: CalendarCheck,
    permission: 'attendance:read',
    children: [
      { key: 'attendance.daily', to: '/attendance/daily', permission: 'attendance:read', anyPermission: ['attendance:read-all', 'attendance:approve'] },
      { key: 'attendance.roster', to: '/attendance/roster', permission: 'attendance:read', anyPermission: ['attendance:read-all', 'attendance:approve'] },
      { key: 'attendance.holidays', to: '/attendance/holidays', permission: 'attendance:read' },
      { key: 'attendance.regularisation', to: '/attendance/regularisation', permission: 'attendance:approve' },
      { key: 'attendance.devices', to: '/attendance/devices', permission: 'device:read' },
      { key: 'attendance.kiosks', to: '/attendance/kiosks', permission: 'attendance:write', anyPermission: ['attendance:read-all'] },
      { key: 'attendance.geofencing', to: '/attendance/geofencing', permission: 'attendance:read-all' },
      { key: 'attendance.sources', to: '/attendance/sources', permission: 'device:read' },
      { key: 'attendance.feed', to: '/attendance/feed', permission: 'device:read' },
      { key: 'attendance.reports', to: '/attendance/reports', permission: 'attendance:read-all' },
    ],
  },
  {
    key: 'leave',
    icon: CalendarDays,
    permission: 'leave:read',
    children: [
      { key: 'leave.requests', to: '/leave', permission: 'leave:read' },
      { key: 'leave.balances', to: '/leave/balances', permission: 'leave:read', anyPermission: ['leave:read-all', 'leave:approve', 'leave:configure'] },
      { key: 'leave.types', to: '/leave/types', permission: 'leave:configure' },
    ],
  },
  {
    key: 'timesheets',
    icon: Timer,
    permission: 'timesheet:read',
    children: [
      { key: 'timesheets.myWeek', to: '/timesheets', permission: 'timesheet:read' },
      { key: 'timesheets.projects', to: '/timesheets/projects', permission: 'timesheet:read' },
      { key: 'timesheets.approvals', to: '/timesheets/approvals', permission: 'timesheet:approve' },
    ],
  },
  {
    key: 'payroll',
    icon: Wallet,
    // The section opens for anybody with payroll:read — their own payslips are in it — but
    // each run-level entry needs payroll:read-all, so an employee is not shown a screen that
    // would refuse them.
    permission: 'payroll:read',
    children: [
      { key: 'payroll.myPayslips', to: '/payroll/my-payslips', permission: 'payroll:read' },
      { key: 'payroll.runs', to: '/payroll/runs', permission: 'payroll:read-all' },
      { key: 'payroll.elements', to: '/payroll/elements', permission: 'payroll:read-all' },
      { key: 'payroll.provisions', to: '/payroll/provisions', permission: 'payroll:read-all' },
      { key: 'payroll.chartOfAccounts', to: '/payroll/chart-of-accounts', permission: 'compensation:read' },
      { key: 'payroll.wps', to: '/payroll/wps', permission: 'payment:read' },
      { key: 'payroll.payments', to: '/payroll/payments', permission: 'payment:read' },
      { key: 'payroll.bankChannels', to: '/payroll/bank-channels', permission: 'payment:bank-channel' },
      { key: 'payroll.reports', to: '/payroll/reports', permission: 'payroll:read-all' },
    ],
  },
  {
    key: 'performance',
    icon: Target,
    permission: 'performance:read',
    children: [
      { key: 'performance.appraisals', to: '/performance/appraisals', permission: 'performance:read' },
      { key: 'performance.goals', to: '/performance/goals', permission: 'goal:read' },
      { key: 'performance.calibration', to: '/performance/calibration', anyPermission: ['performance:calibrate', 'performance:configure'] },
      { key: 'performance.plans', to: '/performance/plans', permission: 'pip:manage' },
      { key: 'performance.trainingNeeds', to: '/performance/training-needs', permission: 'training:read' },
      { key: 'performance.setup', to: '/performance/setup', permission: 'performance:configure' },
      { key: 'performance.trainingCatalogue', to: '/training/catalogue', permission: 'training:read' },
      { key: 'performance.trainingSessions', to: '/training/sessions', permission: 'training:read' },
      { key: 'performance.trainingBudgets', to: '/training/budgets', anyPermission: ['training:configure', 'training:approve', 'training:write'] },
    ],
  },
  {
    key: 'compensation',
    icon: Scale,
    permission: 'compensation:read',
    children: [
      { key: 'compensation.bands', to: '/compensation/bands', permission: 'compensation:read' },
      { key: 'compensation.revisions', to: '/compensation/revisions', permission: 'compensation:read' },
      { key: 'compensation.benefits', to: '/benefits', permission: 'benefit:read' },
    ],
  },
  {
    key: 'loans',
    icon: HandCoins,
    permission: 'loan:read',
    children: [
      { key: 'loans.all', to: '/loans', permission: 'loan:read' },
      { key: 'loans.types', to: '/loans/types', permission: 'loan:read' },
    ],
  },
  {
    key: 'settlements',
    icon: Handshake,
    permission: 'settlement:read',
    children: [
      { key: 'settlements.all', to: '/settlements', permission: 'settlement:read' },
      { key: 'settlements.terms', to: '/settings/settlement-terms', permission: 'settlement:read' },
    ],
  },
  {
    key: 'expenses',
    icon: ReceiptText,
    permission: 'expense:read',
    children: [
      { key: 'expenses.claims', to: '/expenses/claims', permission: 'expense:read' },
      { key: 'expenses.types', to: '/expenses/types', permission: 'expense:read' },
      { key: 'expenses.travel', to: '/travel/requests', permission: 'travel:read' },
      { key: 'expenses.perDiem', to: '/travel/per-diem', permission: 'travel:read' },
    ],
  },
  {
    key: 'assets',
    icon: Package,
    permission: 'asset:read',
    children: [
      { key: 'assets.register', to: '/assets', permission: 'asset:read', anyPermission: ['asset:write', 'asset:assign', 'asset:configure', 'asset:audit', 'asset:dispose'] },
      { key: 'assets.categories', to: '/assets/categories', permission: 'asset:read', anyPermission: ['asset:write', 'asset:assign', 'asset:configure', 'asset:audit', 'asset:dispose'] },
      { key: 'assets.stocktakes', to: '/assets/stocktakes', permission: 'asset:read', anyPermission: ['asset:write', 'asset:assign', 'asset:configure', 'asset:audit', 'asset:dispose'] },
      { key: 'assets.reports', to: '/assets/reports', permission: 'asset:read', anyPermission: ['asset:write', 'asset:assign', 'asset:configure', 'asset:audit', 'asset:dispose'] },
    ],
  },
  {
    key: 'documents',
    icon: FileText,
    permission: 'document:read',
    children: [
      { key: 'documents.expiring', to: '/documents/expiring', permission: 'employee:read-all' },
      { key: 'documents.company', to: '/documents/company', permission: 'document:read' },
      { key: 'documents.types', to: '/documents/types', permission: 'document:write' },
      { key: 'documents.signatures', to: '/documents/signatures', permission: 'esign:read' },
      { key: 'documents.credentialRules', to: '/documents/credential-rules', permission: 'compliance:read' },
      { key: 'documents.letters', to: '/documents/letters', permission: 'letter:generate' },
      { key: 'documents.library', to: '/library', permission: 'document:read' },
      { key: 'documents.policies', to: '/library/policies', permission: 'document:read' },
      { key: 'documents.librarySetup', to: '/library/setup', permission: 'library:configure' },
    ],
  },
  {
    key: 'helpdesk',
    icon: LifeBuoy,
    permission: 'helpdesk:read',
    children: [
      { key: 'helpdesk.queue', to: '/helpdesk', permission: 'helpdesk:read' },
      { key: 'helpdesk.knowledgeBase', to: '/helpdesk/knowledge-base', permission: 'helpdesk:read' },
      { key: 'helpdesk.setup', to: '/helpdesk/setup', permission: 'helpdesk:configure' },
    ],
  },
  {
    key: 'engagement',
    icon: Megaphone,
    permission: 'engagement:read',
    children: [
      { key: 'engagement.announcements', to: '/engagement/announcements', permission: 'engagement:read' },
      { key: 'engagement.surveys', to: '/engagement/surveys', permission: 'engagement:read' },
      { key: 'engagement.recognition', to: '/engagement/recognition', permission: 'engagement:read' },
    ],
  },
  {
    key: 'requests',
    icon: Inbox,
    permission: 'request:read',
    children: [
      { key: 'requests.all', to: '/requests', permission: 'request:read' },
      { key: 'requests.mine', to: '/requests/my', permission: 'request:read' },
      { key: 'requests.types', to: '/requests/types', permission: 'request:configure' },
    ],
  },
  {
    key: 'pro',
    icon: Stamp,
    permission: 'pro:read',
    children: [
      { key: 'pro.cases', to: '/pro/cases', permission: 'pro:read' },
      { key: 'pro.processes', to: '/pro/processes', permission: 'pro:configure' },
      { key: 'pro.reports', to: '/pro/reports', permission: 'pro:read' },
    ],
  },
  {
    key: 'exits',
    icon: DoorOpen,
    permission: 'exit:read',
    children: [
      { key: 'exits.cases', to: '/exit', permission: 'exit:read' },
      { key: 'exits.settings', to: '/exit/settings', permission: 'exit:write' },
    ],
  },
  {
    key: 'compliance',
    icon: ScrollText,
    permission: 'compliance:read',
    children: [
      { key: 'compliance.calendar', to: '/compliance', permission: 'compliance:read' },
      { key: 'compliance.items', to: '/compliance/items', permission: 'compliance:read' },
      { key: 'compliance.nationalisation', to: '/compliance/nationalisation', permission: 'compliance:read' },
      { key: 'compliance.filings', to: '/compliance/filings', permission: 'compliance:read' },
      { key: 'compliance.retention', to: '/compliance/retention', permission: 'compliance:read' },
      { key: 'compliance.requests', to: '/compliance/requests', permission: 'compliance:read' },
    ],
  },
  {
    key: 'reports',
    icon: BarChart3,
    permission: 'report:read',
    children: [
      { key: 'reports.exports', to: '/reports/exports', permission: 'report:export-configure' },
      { key: 'reports.biAccess', to: '/reports/bi-access', permission: 'bi:read' },
      { key: 'reports.catalogue', to: '/reports/catalogue', permission: 'report:read' },
      { key: 'reports.builder', to: '/reports/builder', permission: 'report:build' },
    ],
  },
  {
    key: 'settings',
    icon: Settings,
    permission: 'settings:read',
    children: [
      { key: 'settings.general', to: '/settings', permission: 'settings:read' },
      { key: 'settings.dataMigration', to: '/settings/data-migration', permission: 'settings:read' },
      { key: 'settings.branding', to: '/settings/branding', permission: 'admin:branding' },
      { key: 'settings.customFields', to: '/settings/custom-fields', permission: 'settings:read' },
      { key: 'settings.notifications', to: '/settings/notifications', permission: 'settings:read' },
      { key: 'settings.banks', to: '/settings/banks', permission: 'settings:read' },
      { key: 'settings.ai', to: '/settings/ai', permission: 'ai:configure' },
      { key: 'settings.grants', to: '/settings/grants', permission: 'admin:grants' },
      { key: 'settings.segregation', to: '/settings/segregation-of-duties', permission: 'admin:grants' },
      { key: 'settings.sso', to: '/settings/sso', permission: 'integration:read' },
      { key: 'settings.erpExport', to: '/settings/erp-export', permission: 'erp:export' },
      { key: 'settings.apiClients', to: '/settings/api-clients', permission: 'integration:read' },
      { key: 'settings.webhooks', to: '/settings/webhooks', permission: 'integration:read' },
    ],
  },
  {
    key: 'administration',
    icon: ShieldCheck,
    permission: 'admin:system',
    children: [
      { key: 'administration.users', to: '/administration/users', permission: 'admin:user_manage' },
      { key: 'administration.roles', to: '/administration/roles', permission: 'admin:role_manage' },
      { key: 'administration.sessions', to: '/administration/sessions', permission: 'admin:user_manage' },
      { key: 'administration.accessLog', to: '/administration/access-log', permission: 'admin:access-log' },
      { key: 'administration.auditLog', to: '/administration/audit-log', permission: 'audit:read' },
      { key: 'administration.workflows', to: '/administration/workflows', permission: 'workflow:configure' },
      { key: 'administration.health', to: '/administration/health', permission: 'admin:system' },
    ],
  },
]

/** Everything reachable, flattened — for breadcrumbs and the page title. */
export interface NavMatch {
  module: NavItem
  child?: NavChild
}

/**
 * The deepest nav entry matching a path.
 *
 * <p>Longest match wins, so `/attendance/devices` resolves to the Devices child rather than
 * to whichever child happens to be listed first. The dashboard is matched exactly, because
 * every path starts with `/`.
 */
export function matchNav(pathname: string): NavMatch | undefined {
  let best: NavMatch | undefined
  let bestLength = -1

  for (const module of NAV_ITEMS) {
    if (module.to && covers(module.to, pathname) && module.to.length > bestLength) {
      best = { module }
      bestLength = module.to.length
    }
    for (const child of module.children ?? []) {
      if (covers(child.to, pathname) && child.to.length > bestLength) {
        best = { module, child }
        bestLength = child.to.length
      }
    }
  }
  return best
}

function covers(to: string, pathname: string): boolean {
  if (to === '/') {
    return pathname === '/'
  }
  return pathname === to || pathname.startsWith(to + '/')
}
